import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

function formatoPesos(value) {
    return '$ ' + value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function FormatoFechaConHora(value) {
    const fecha = new Date(value);
    return format(fecha, "dd-MMMM-yyyy HH:mm:ss", { locale: es }).toUpperCase();
}

function calcularAnchoProporcional(base64, alturaFija) {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const ancho = img.naturalWidth && img.naturalHeight
                ? (img.naturalWidth * alturaFija) / img.naturalHeight
                : 50;
            resolve(ancho);
        };
        img.onerror = () => resolve(50);
        img.src = base64;
    });
}

function detectarFormatoImagen(base64) {
    if (base64.startsWith('data:image/png'))  return 'PNG';
    if (base64.startsWith('data:image/jpeg')) return 'JPEG';
    if (base64.startsWith('data:image/jpg'))  return 'JPEG';
    if (base64.startsWith('data:image/gif'))  return 'GIF';
    if (base64.startsWith('data:image/webp')) return 'WEBP';
    return 'PNG';
}

const FlujoPDF = async (
    estadoResultado,
    actividadesOperacion,
    actividadesInversion,
    actividadesFinanciamiento,
    importePorAplicar,
    variacionFlujo,
    efectivoInicioEjercicio,
    flujoEfectivoFinalPeriodo,
    objEmpresa,
    periodo
) => {
    return new Promise(async (resolve, reject) => {
        try {
            const doc = new jsPDF({ format: 'letter' });

            const color               = objEmpresa?.color || '#662e91';
            const logoBase64          = objEmpresa?.logo?.base64 || '';
            const fechaHoraGeneracion = FormatoFechaConHora(new Date());

            const pageW  = doc.internal.pageSize.getWidth();
            const pageH  = doc.internal.pageSize.getHeight();
            const margen = 12;
            const xDer   = pageW - margen;
            const ancho  = xDer - margen;

            // ── CABECERA ──────────────────────────────────────────────────
            let y = 14;

            if (logoBase64 !== '') {
                const alturaLogo = 20;
                const formatoImg = detectarFormatoImagen(logoBase64);
                const anchoLogo  = await calcularAnchoProporcional(logoBase64, alturaLogo);
                doc.addImage(logoBase64, formatoImg, margen, y, anchoLogo, alturaLogo);
            }

            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor('#000000');
            doc.text('Estado de Flujo de Efectivo', xDer, y, { align: 'right' });

            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.text((objEmpresa?.rfc || '') + ' | ' + (objEmpresa?.nombre || objEmpresa?.empresa || ''), xDer, y += 6, { align: 'right' });
            doc.text(periodo || '', xDer, y += 5, { align: 'right' });

            y += 10;

            // ── Helper: barra de totales ──────────────────────────────────
            const barraTotal = (label, valor, yPos) => {
                doc.setFillColor(color);
                doc.rect(margen, yPos, ancho, 8, 'F');
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(9);
                doc.setTextColor('#ffffff');
                doc.text(label, margen + 3, yPos + 5.5, { align: 'left' });
                doc.text(formatoPesos(valor), xDer, yPos + 5.5, { align: 'right' });
                return yPos + 12;
            };

            // ── Helper: tabla de conceptos ────────────────────────────────
            const tablaConceptos = (rows, startY) => {
                doc.autoTable({
                    startY,
                    head: [[ 
                        { content: 'Cuenta',  styles: { halign: 'left'  } },
                        { content: 'Importe', styles: { halign: 'right' } },
                    ]],
                    body: rows.map(r => [
                        { content: r.cuenta,              styles: { halign: 'left'  } },
                        { content: formatoPesos(r.total), styles: { halign: 'right' } },
                    ]),
                    styles: { fontSize: 9, cellPadding: 2.5 },
                    headStyles: { fillColor: color, textColor: '#ffffff', fontStyle: 'bold' },
                    alternateRowStyles: { fillColor: '#f4f4f4' },
                    columnStyles: {
                        0: { cellWidth: ancho * 0.72 },
                        1: { cellWidth: ancho * 0.28 },
                    },
                    margin: { left: margen, right: margen },
                });
                return doc.lastAutoTable.finalY + 6;
            };

            // ── UTILIDAD NETA ─────────────────────────────────────────────
            y = barraTotal('UTILIDAD NETA', estadoResultado, y);

            // ── ACTIVIDADES DE OPERACIÓN ──────────────────────────────────
            const sumOperacion = actividadesOperacion.reduce((a, b) => a + b.total, 0);

            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(color);
            doc.text('ACTIVIDADES DE OPERACIÓN', margen, y += 3);
            doc.setFontSize(11);
            doc.setTextColor('#000000');
            doc.text(formatoPesos(sumOperacion), xDer, y, { align: 'right' });
            doc.setDrawColor(color);
            doc.setLineWidth(0.4);
            doc.line(margen, y + 1, xDer, y + 1);
            y += 5;

            y = tablaConceptos(actividadesOperacion, y);

            // ── ACTIVIDADES DE INVERSIÓN ──────────────────────────────────
            const sumInversion = actividadesInversion.reduce((a, b) => a + b.total, 0);

            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(color);
            doc.text('ACTIVIDADES DE INVERSIÓN', margen,  y += 3);
            doc.setTextColor('#000000');
            doc.text(formatoPesos(sumInversion), xDer, y, { align: 'right' });
            doc.setDrawColor(color);
            doc.line(margen, y + 1, xDer, y + 1);
            y += 5;

            y = tablaConceptos(actividadesInversion, y);

            // ── IMPORTE POR APLICAR ───────────────────────────────────────
            y = barraTotal('IMPORTE POR APLICAR EN ACTIVIDADES DE FINANCIAMIENTO', importePorAplicar, y);

            // ── ACTIVIDADES DE FINANCIAMIENTO ─────────────────────────────
            const sumFinanciamiento = actividadesFinanciamiento.reduce((a, b) => a + b.total, 0);

            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(color);
            doc.text('ACTIVIDADES DE FINANCIAMIENTO', margen,  y += 3);
            doc.setTextColor('#000000');
            doc.text(formatoPesos(sumFinanciamiento), xDer, y, { align: 'right' });
            doc.setDrawColor(color);
            doc.line(margen, y + 1, xDer, y + 1);
            y += 5;

            y = tablaConceptos(actividadesFinanciamiento, y);

            // ── TOTALES FINALES ───────────────────────────────────────────
            y = barraTotal('VARIACIONES EN FLUJO',                   variacionFlujo,              y);
            y = barraTotal('EFECTIVO AL INICIO DEL EJERCICIO',       efectivoInicioEjercicio,     y);
            y = barraTotal('FLUJO DE EFECTIVO AL FINAL DEL PERIODO', flujoEfectivoFinalPeriodo,   y);

            // ── PIE DE PÁGINA ─────────────────────────────────────────────
            const totalPages = doc.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
                doc.setPage(i);
                doc.setFont('helvetica', 'italic');
                doc.setFontSize(8);
                doc.setTextColor(150);
                doc.setDrawColor(color);
                doc.setLineWidth(0.3);
                doc.line(margen, pageH - 16, xDer, pageH - 16);
                doc.text('Generado: ' + fechaHoraGeneracion, margen, pageH - 8, { align: 'left' });
                doc.text('Documento generado por ContaGo', pageW / 2, pageH - 8, { align: 'center' });
                doc.text('Página ' + i + ' de ' + totalPages, xDer, pageH - 8, { align: 'right' });
            }

            doc.save('Flujo_de_Efectivo_' + (objEmpresa?.rfc || '') + '.pdf');
            resolve();
        } catch (error) {
            console.error(error);
            reject(error);
        }
    });
};

export { FlujoPDF };